import { useQueryClient } from '@tanstack/react-query';
import { useParentScrollableRef } from '@unistocks/components/basic/scrollview';
import { SubmitButtons } from '@unistocks/components/button/submit-buttons';
import { SwitchButton } from '@unistocks/components/switch/switch-button';
import {
  DraggableAsset,
  DraggableSection
} from '@unistocks/features/portfolios/components/portfolio-settings/sort/draggable-item';
import { sortingReducer } from '@unistocks/features/portfolios/components/portfolio-settings/sort/reducer';
import {
  SectionListItem,
  SortablePortfolioItem
} from '@unistocks/features/portfolios/components/portfolio-settings/sort/types';
import { PortfolioBase } from '@unistocks/features/portfolios/models/portfolio-base';
import { isPortfolio } from '@unistocks/features/portfolios/portfolio-utils';
import { t } from '@unistocks/localization';
import { SectionAssetDto } from '@unistocks/proxy/axios-client';
import {
  setPortfoliosGetData,
  usePortfoliosSortMutation
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import {
  setWatchlistsGetData,
  useWatchlistsSortMutation
} from '@unistocks/proxy/axios-client/WatchlistsQuery';
import { extractId } from '@unistocks/shared/utils/list-utils';
import React, { useCallback, useEffect, useReducer } from 'react';
import Sortable, { DragEndCallback, SortableGridRenderItem } from 'react-native-sortables';

export type SortPortfolioProps = {
  portfolio: PortfolioBase;
  onSave: () => void;
  onCancel?: () => void;
};

export const SortPortfolio: React.FC<SortPortfolioProps> = ({ portfolio, onCancel, onSave }) => {
  const queryClient = useQueryClient();
  const scrollRef = useParentScrollableRef();
  const [{ data, mode, sections }, dispatch] = useReducer(sortingReducer, {
    mode: 'holdings',
    portfolio,
    sections: [],
    data: []
  });
  const isCategoriesMode = mode === 'categories';

  useEffect(() => {
    dispatch({ type: 'INIT', portfolio });
  }, [portfolio]);

  const toggleAutoSort = (sectionId: string) => {
    dispatch({ type: 'TOGGLE_AUTO_SORT', sectionId });
  };

  const setSectionOrder = (sectionOrder: string[]) => {
    dispatch({ type: 'SET_SECTION_ORDER', sectionOrder });
  };

  const toggleMode = () => {
    dispatch({ type: 'TOGGLE_MODE' });
  };

  const { mutate: sortPortfolio, isPending: isPendingPortfolioMutation } =
    usePortfoliosSortMutation(portfolio.id, {
      onSuccess: data => {
        onSave();
        setPortfoliosGetData(queryClient, () => data, portfolio.id);
      }
    });

  const { mutate: sortWatchlist, isPending: isPendingWatchlistMutation } =
    useWatchlistsSortMutation(portfolio.id, {
      onSuccess: data => {
        onSave();
        setWatchlistsGetData(queryClient, () => data, portfolio.id);
      }
    });

  const onSavePress = () => {
    if (!sections.length) return;

    const sectionSorting = sections.map((section, index) => ({
      sectionId: section.id,
      sorting: {
        ...section.sorting,
        order: index
      },
      assets: section.assetIds
    }));

    (isPortfolio(portfolio) ? sortPortfolio : sortWatchlist)({ sections: sectionSorting });
  };

  const onDragEnd = useCallback<DragEndCallback>(
    ({ indexToKey }) => {
      if (isCategoriesMode) {
        setSectionOrder(indexToKey);
      } else {
        dispatch({ type: 'SET_SORT_ORDER', sortOrder: indexToKey });
      }
    },
    [isCategoriesMode]
  );

  const renderItem = useCallback<
    SortableGridRenderItem<SortablePortfolioItem<'asset' | 'section'>>
  >(
    ({ item: { item, type }, index }) =>
      type === 'section' ? (
        <DraggableSection
          section={item as SectionListItem}
          index={index}
          isDraggable={isCategoriesMode}
          toggleAutoSort={() => toggleAutoSort(item.id)}
        />
      ) : (
        <DraggableAsset asset={item as SectionAssetDto} />
      ),
    [isCategoriesMode]
  );

  const isPending = isPendingPortfolioMutation || isPendingWatchlistMutation;

  return (
    <>
      <SwitchButton
        leftLabel={t('::Holdings')}
        rightLabel={t('::Categories')}
        mx={'auto'}
        checked={isCategoriesMode}
        onCheckedChange={toggleMode}
      />
      <Sortable.Grid
        scrollableRef={scrollRef}
        hapticsEnabled
        showDropIndicator
        customHandle
        sortEnabled={!isPending}
        bringToFrontWhenActive
        data={data}
        keyExtractor={extractId}
        renderItem={renderItem}
        overDrag="vertical"
        activeItemScale={0.9}
        onDragEnd={onDragEnd}
      />
      <SubmitButtons
        cancelButtonText={t('::Cancel')}
        submitButtonText={t('::Save')}
        onCancel={onCancel}
        onSubmit={onSavePress}
        isSubmitting={isPending}
      />
    </>
  );
};

SortPortfolio.displayName = 'SortPortfolio';
